import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle, FileText, MapPin, LocateFixed, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import BarcodeInput from '@/components/BarcodeInput';
import ScanFeedbackOverlay from '@/components/ScanFeedbackOverlay';
import ImageCaptureGrid from '@/components/ImageCaptureGrid';
import InwardDataTable from '@/components/InwardDataTable';
import OnlineStatusBadge from '@/components/OnlineStatusBadge';
import SyncStatusIndicator from '@/components/SyncStatusIndicator';
import { offlineStorage } from '@/services/offlineStorage';
import { validateStaffId } from '@/services/validationService';
import { verifyAdmin } from '@/services/authStepUp';
import { compressImageFiles } from '@/utils/imageCompression';
import {
  validateDocumentCode,
  validateBundleCode,
  uploadImages,
  createInwardEntry,
  submitDraftEntry,
  updateInwardEntry,
  deleteInwardEntry
} from '@/services/api';

const InwardForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const staffIdRef = useRef(null);
  const documentBarcodeRef = useRef(null);
  const bundleBarcodeRef = useRef(null);

  // Check if editing a draft
  const draftEntry = location.state?.draftEntry;
  const isEditingDraft = !!draftEntry;

  // Form state
  const [staffId, setStaffId] = useState(draftEntry?.staff_id || '');
  const [documentBarcode, setDocumentBarcode] = useState(draftEntry?.document_barcode || '');
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [images, setImages] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState(draftEntry?.image_urls || []);
  const [bundles, setBundles] = useState(draftEntry?.bundles || []);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(isEditingDraft ? 'bundles' : 'staff_id');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [createdEntryId, setCreatedEntryId] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Auto-save tracking
  const [lastAutoSaveCount, setLastAutoSaveCount] = useState(0);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [autoSavedEntryId, setAutoSavedEntryId] = useState(draftEntry?.entry_id || '');

  // 2026-06 — STRICT GPS-only gate. Capture the device GPS at the gate; the
  // backend tags the nearest warehouse. If GPS is denied/unavailable, submission
  // is hard-blocked (Retry GPS) unless an admin re-verifies and overrides.
  const [geoCoords, setGeoCoords] = useState({ latitude: null, longitude: null });
  const [geoStatus, setGeoStatus] = useState('idle'); // idle|requesting|granted|denied|unavailable
  const [geoError, setGeoError] = useState('');
  const [gpsOverride, setGpsOverride] = useState(null); // {by, reason}

  // Admin GPS-override dialog
  const [showGpsOverride, setShowGpsOverride] = useState(false);
  const [ovEmail, setOvEmail] = useState('');
  const [ovPassword, setOvPassword] = useState('');
  const [ovReason, setOvReason] = useState('');
  const [ovVerifying, setOvVerifying] = useState(false);

  const requestGps = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      setGeoError('This device does not support location (GPS).');
      return;
    }
    setGeoStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setGeoStatus('granted');
        setGeoError('');
      },
      (err) => {
        setGeoCoords({ latitude: null, longitude: null });
        setGeoStatus(err && err.code === 1 ? 'denied' : 'unavailable');
        setGeoError(err?.message || 'Could not read device location.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => { requestGps(); }, [requestGps]);

  const hasGps = geoCoords.latitude != null && geoCoords.longitude != null;

  const handleGpsOverride = async () => {
    if (!ovEmail.trim() || !ovPassword) {
      toast.error('Enter admin email and password');
      return;
    }
    if (!ovReason.trim()) {
      toast.error('A reason is required for the override');
      return;
    }
    setOvVerifying(true);
    try {
      const res = await verifyAdmin(ovEmail.trim(), ovPassword);
      setGpsOverride({ by: res.admin_email, reason: ovReason.trim() });
      setShowGpsOverride(false);
      setOvEmail(''); setOvPassword(''); setOvReason('');
      toast.success(`GPS override approved by ${res.admin_name || res.admin_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Admin verification failed');
    } finally {
      setOvVerifying(false);
    }
  };

  // OPTIMIZATION: Create a Set of bundle barcodes for O(1) duplicate checking
  const bundleSet = useMemo(() => new Set(bundles.map(b => b.bundle_barcode)), [bundles]);

  // Auto-save function (silent, non-blocking)
  const performAutoSave = useCallback(async () => {
    if (!staffId || !documentBarcode || isAutoSaving || isSavingDraft || isSubmitting) {
      return;
    }

    setIsAutoSaving(true);
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      
      const timestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      
      // Use existing auto-saved entry ID or create new one
      const entryId = autoSavedEntryId || (isEditingDraft ? draftEntry.entry_id : 
        `INW${year}${month}${day}${hours}${minutes}${seconds}${bundles.length.toString().padStart(3, '0')}`);

      const entryData = {
        entry_id: entryId,
        staff_id: staffId,
        document_barcode: documentBarcode,
        image_urls: existingImageUrls,
        bundles: bundles,
        timestamp: timestamp,
        status: 'draft'
      };

      if (autoSavedEntryId || isEditingDraft) {
        // Update existing draft
        const updateData = {
          bundles: bundles,
          image_urls: existingImageUrls
        };
        await updateInwardEntry(autoSavedEntryId || draftEntry.entry_id, updateData);
      } else {
        // Create new draft
        const response = await createInwardEntry(entryData);
        // Use the entry_id returned by the backend, not the frontend-generated one
        const serverEntryId = response?.entry_id || entryId;
        setAutoSavedEntryId(serverEntryId);
      }
      
      setLastAutoSaveCount(bundles.length);
      // Show subtle notification that doesn't interrupt workflow
      toast.success(`Auto-saved at ${bundles.length} bundles`, { 
        duration: 2000,
        position: 'bottom-right',
        style: { fontSize: '12px', padding: '8px 12px' }
      });
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Silent failure - don't interrupt the user
    } finally {
      setIsAutoSaving(false);
    }
  }, [staffId, documentBarcode, bundles, existingImageUrls, autoSavedEntryId, isEditingDraft, draftEntry, isAutoSaving, isSavingDraft, isSubmitting]);

  // Auto-save every 25 bundles
  useEffect(() => {
    const bundleCount = bundles.length;
    // Trigger auto-save at multiples of 25 (25, 50, 75, etc.)
    if (bundleCount > 0 && bundleCount % 25 === 0 && bundleCount !== lastAutoSaveCount) {
      performAutoSave();
    }
  }, [bundles.length, lastAutoSaveCount, performAutoSave]);

  // Auto-focus management
  useEffect(() => {
    if (step === 'staff_id' && staffIdRef.current) {
      staffIdRef.current.focus();
    } else if (step === 'document_barcode' && documentBarcodeRef.current) {
      documentBarcodeRef.current.focus();
    } else if (step === 'bundles' && bundleBarcodeRef.current) {
      bundleBarcodeRef.current.focus();
    }
  }, [step]);

  const showFeedback = (type, message) => {
    setScanFeedback({ type, message });
    setTimeout(() => setScanFeedback(null), 350);
  };

  const handleStaffIdScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, staffId: 'Staff ID is required' });
      showFeedback('error', 'Staff ID Required');
      return;
    }

    try {
      // Validate against staff master
      const result = await validateStaffId(value.trim());
      if (!result.valid) {
        setErrors({ ...errors, staffId: result.message || 'Invalid Staff ID - not found in Staff Master' });
        showFeedback('error', 'Invalid Staff ID');
        setStaffId('');
        return;
      }

      setErrors({ ...errors, staffId: null });
      showFeedback('success', `Staff: ${result.staff_name || value}`);
      setStep('document_barcode');
    } catch (error) {
      console.error('Staff validation error:', error);
      setErrors({ ...errors, staffId: 'Failed to validate Staff ID' });
      showFeedback('error', 'Validation Error');
    }
  };

  const handleDocumentBarcodeScan = async (value) => {
    const pattern = /^(?:[A-Z]{2,5}-)?DOC\d{6}\/\d{2}-\d{2}$/;
    if (!pattern.test(value)) {
      setErrors({ ...errors, documentBarcode: 'Invalid format. Expected DOCXXXXXX/YY-YY' });
      showFeedback('error', 'Invalid Format');
      setDocumentBarcode('');
      return;
    }

    try {
      const validation = await validateDocumentCode(value);
      if (!validation.valid) {
        setErrors({ ...errors, documentBarcode: validation.message });
        showFeedback('error', 'Duplicate Code');
        setDocumentBarcode('');
        return;
      }

      setErrors({ ...errors, documentBarcode: null });
      showFeedback('success', 'Document Code Validated');
      setStep('images');
    } catch (error) {
      setErrors({ ...errors, documentBarcode: 'Validation failed. Please try again.' });
      showFeedback('error', 'Validation Error');
      setDocumentBarcode('');
    }
  };

  const handleBundleBarcodeScan = async (value) => {
    const pattern = /^(?:[A-Z]{2,5}-)?BUN\d{6}\/\d{2}-\d{2}$/;
    if (!pattern.test(value)) {
      setErrors({ ...errors, bundleBarcode: 'Invalid format. Expected BUNXXXXXX/YY-YY' });
      showFeedback('error', 'Invalid Format');
      setBundleBarcode('');
      return;
    }

    // OPTIMIZATION: O(1) duplicate check using Set instead of O(n) array scan
    if (bundleSet.has(value)) {
      setErrors({ ...errors, bundleBarcode: 'Bundle already scanned in this entry' });
      showFeedback('error', 'Duplicate Bundle');
      setBundleBarcode('');
      return;
    }

    try {
      const validation = await validateBundleCode(value);
      if (!validation.valid) {
        setErrors({ ...errors, bundleBarcode: validation.message });
        showFeedback('error', 'Duplicate in Database');
        setBundleBarcode('');
        return;
      }

      const newBundle = {
        bundle_barcode: value,
        timestamp: new Date().toISOString(),
        is_re_entry: validation.is_re_entry || false,
        outward_entry_id: validation.outward_entry_id || null
      };
      setBundles([newBundle, ...bundles]);
      setBundleBarcode('');
      setErrors({ ...errors, bundleBarcode: null });
      
      // Show appropriate feedback for new vs re-entry bundles
      if (validation.is_re_entry) {
        showFeedback('success', 'Re-Entry Bundle');
        toast.info(`Re-Entry: Previously dispatched in ${validation.outward_entry_id}`);
      } else {
        showFeedback('success', 'Bundle Added');
      }

      setTimeout(() => {
        if (bundleBarcodeRef.current) {
          bundleBarcodeRef.current.focus();
        }
      }, 350);
    } catch (error) {
      setErrors({ ...errors, bundleBarcode: 'Validation failed. Please try again.' });
      showFeedback('error', 'Validation Error');
      setBundleBarcode('');
    }
  };

  const handleDeleteBundle = (index) => {
    const newBundles = bundles.filter((_, i) => i !== index);
    setBundles(newBundles);
    toast.success('Bundle removed from entry');
  };

  const prepareEntryData = async (status) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    const timestamp = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    const entryId = isEditingDraft ? draftEntry.entry_id : 
      `${navigator.onLine ? 'INW' : 'OFF'}${year}${month}${day}${hours}${minutes}${seconds}${bundles.length.toString().padStart(3, '0')}`;

    return {
      entry_id: entryId,
      staff_id: staffId,
      document_barcode: documentBarcode,
      image_urls: existingImageUrls, // Preserve existing images
      bundles: bundles,
      timestamp: timestamp,
      status: status,
      latitude: geoCoords.latitude,
      longitude: geoCoords.longitude,
      gps_override: !!gpsOverride,
      gps_override_reason: gpsOverride?.reason || null,
      gps_override_by: gpsOverride?.by || null,
    };
  };

  const handleSaveDraft = async () => {
    if (!staffId || !documentBarcode) {
      toast.error('Staff ID and Document Barcode are required for draft');
      return;
    }

    setIsSavingDraft(true);
    try {
      const entryData = await prepareEntryData('draft');

      // Upload new images if any and add to existing
      let finalImageUrls = [...existingImageUrls];
      if (images.length > 0) {
        try {
          toast.info('Uploading images...');
          const rawFiles = images.map(img => img.file);
          const files = await compressImageFiles(rawFiles);
          const uploadResult = await uploadImages(files);
          if (uploadResult?.image_urls?.length > 0) {
            finalImageUrls = [...existingImageUrls, ...uploadResult.image_urls];
          }
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          // Continue saving draft even if images fail
        }
      }
      entryData.image_urls = finalImageUrls;

      // If editing existing draft, update it instead of creating new
      if (isEditingDraft) {
        const updateData = {
          bundles: bundles,
          image_urls: finalImageUrls
        };
        await updateInwardEntry(draftEntry.entry_id, updateData);
        setCreatedEntryId(draftEntry.entry_id);
      } else {
        // Create new draft entry
        await createInwardEntry(entryData);
        setCreatedEntryId(entryData.entry_id);
      }
      
      setSuccessMessage('Draft saved successfully! You can continue editing from the Drafts menu.');
      setShowSuccessDialog(true);

      // Reset form
      resetForm();
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft. Please try again.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we have images (either new ones or existing from draft)
    const hasImages = images.length > 0 || existingImageUrls.length > 0;

    if (!staffId || !documentBarcode || !hasImages || bundles.length === 0) {
      if (!hasImages) {
        toast.error('Please capture at least 1 document image');
      } else if (bundles.length === 0) {
        toast.error('Please scan at least 1 bundle QR code');
      } else {
        toast.error('Please complete all required fields');
      }
      return;
    }

    // 2026-06 STRICT GPS gate — block submit without a GPS fix unless an admin
    // has overridden it. Backend enforces the same rule as a safety net.
    if (!hasGps && !gpsOverride) {
      toast.error('Location (GPS) is required to submit. Tap "Retry GPS" or use Admin override.');
      requestGps();
      return;
    }

    setIsSubmitting(true);
    const isOnline = navigator.onLine;

    try {
      // For draft entries, we need to update and then submit
      if (isEditingDraft) {
        let finalImageUrls = [...existingImageUrls];
        
        // Upload new images if any
        if (images.length > 0) {
          try {
            toast.info('Uploading images to cloud storage...');
            const rawFiles = images.map(img => img.file);
            const files = await compressImageFiles(rawFiles);
            const uploadResult = await uploadImages(files);
            
            if (uploadResult?.image_urls?.length > 0) {
              finalImageUrls = [...existingImageUrls, ...uploadResult.image_urls];
              toast.success(`${uploadResult.image_urls.length} images uploaded successfully`);
            } else {
              throw new Error('Image upload failed - no URLs returned');
            }
          } catch (uploadError) {
            // 2026-05-04 fix: do NOT silently continue on submit. Earlier
            // this catch swallowed image upload failures and the draft
            // was promoted to submitted with image_urls=[], producing
            // ghost entries that never appeared in the Documentation
            // page. Abort the submission and tell the operator.
            console.error('Image upload failed during submit:', uploadError);
            const serverDetail = uploadError?.response?.data?.detail;
            toast.error(
              <div>
                <p className="font-semibold">Image Upload Failed</p>
                <p className="text-sm mt-1">
                  {serverDetail
                    ? `Server says: ${serverDetail}`
                    : 'Entry not submitted. Please retry — at least one image must be attached before submission.'}
                </p>
              </div>,
              { duration: 9000 }
            );
            setIsSubmitting(false);
            return;
          }
        }

        // Hard guard: never submit a draft with zero images.
        if (!finalImageUrls || finalImageUrls.length === 0) {
          toast.error('Cannot submit without at least one document image. Please attach images and try again.');
          setIsSubmitting(false);
          return;
        }

        // Update the draft entry with any changes
        const updateData = {
          bundles: bundles,
          image_urls: finalImageUrls
        };
        
        try {
          await updateInwardEntry(draftEntry.entry_id, updateData);
        } catch (updateError) {
          console.error('Update failed:', updateError);
          const updateErrMsg = updateError.response?.data?.detail || updateError.message || 'Failed to update entry';
          toast.error(`Update failed: ${updateErrMsg}`);
          setIsSubmitting(false);
          return;
        }
        
        // Now submit the draft
        try {
          await submitDraftEntry(draftEntry.entry_id);
        } catch (submitError) {
          console.error('Submit failed:', submitError);
          const submitErrMsg = submitError.response?.data?.detail || submitError.message || 'Failed to submit entry';
          toast.error(`Submit failed: ${submitErrMsg}`);
          setIsSubmitting(false);
          return;
        }
        
        setCreatedEntryId(draftEntry.entry_id);
        setSuccessMessage('Entry has been submitted successfully!');
        setShowSuccessDialog(true);
        resetForm();
        
      } else {
        // New entry flow
        const entryData = await prepareEntryData('submitted');

        // Upload new images if any and combine with existing
        if (images.length > 0) {
          try {
            toast.info('Uploading images to cloud storage...');
            const rawFiles = images.map(img => img.file);
            const files = await compressImageFiles(rawFiles);
            const uploadResult = await uploadImages(files);
            
            if (!uploadResult || !uploadResult.image_urls || uploadResult.image_urls.length === 0) {
              throw new Error('Image upload failed - no URLs returned');
            }
            
            // Combine existing and new image URLs
            entryData.image_urls = [...existingImageUrls, ...uploadResult.image_urls];
            toast.success(`${uploadResult.image_urls.length} images uploaded successfully`);
          } catch (uploadError) {
            console.error('Image upload failed:', uploadError);
            const serverDetail = uploadError?.response?.data?.detail;
            toast.error(
              <div>
                <p className="font-semibold">Image Upload Failed</p>
                <p className="text-sm mt-1">
                  {serverDetail
                    ? `Server says: ${serverDetail}`
                    : 'Entry cannot be submitted without images. Please try again.'}
                </p>
              </div>,
              { duration: 9000 }
            );
            setIsSubmitting(false);
            return;
          }
        }

        // Save to local storage first
        await offlineStorage.saveInwardEntry({ ...entryData, synced: false, created_offline: !isOnline });

        // If online, sync immediately
        if (isOnline) {
          try {
            await createInwardEntry(entryData);
            await offlineStorage.markAsSynced(entryData.entry_id);
            
            // Delete the auto-saved draft if it exists (different from submitted entry)
            if (autoSavedEntryId && autoSavedEntryId !== entryData.entry_id) {
              try {
                await deleteInwardEntry(autoSavedEntryId);
                console.log('Auto-saved draft deleted:', autoSavedEntryId);
              } catch (deleteError) {
                console.warn('Failed to delete auto-saved draft:', deleteError);
                // Non-critical - don't fail the submission
              }
            }
            
            setCreatedEntryId(entryData.entry_id);
            setSuccessMessage('Entry has been saved successfully with all images uploaded to cloud storage.');
            setShowSuccessDialog(true);
          } catch (syncError) {
            console.error('Failed to sync immediately:', syncError);
            toast.warning(
              <div>
                <p className="font-semibold">Entry Saved Locally</p>
                <p className="text-sm mt-1">Will sync when connection is restored</p>
              </div>,
              { duration: 5000 }
            );
          }
        } else {
          toast.success(
            <div>
              <p className="font-semibold">Entry Saved Offline</p>
              <p className="text-sm mt-1">Will sync automatically when online</p>
            </div>,
            { duration: 5000 }
          );
        }

        resetForm();
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      // Show detailed error message
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save entry. Please try again.';
      toast.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setStaffId('');
    setDocumentBarcode('');
    setBundleBarcode('');
    setImages([]);
    setExistingImageUrls([]);
    setBundles([]);
    setErrors({});
    setStep('staff_id');

    setTimeout(() => {
      if (staffIdRef.current) {
        staffIdRef.current.focus();
      }
    }, 500);
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all data?')) {
      resetForm();
      toast.info('Form cleared');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">
                {isEditingDraft ? 'Edit Draft Entry' : 'Inward Entry Form'}
              </h1>
              <p className="text-sm text-[#E8DCC8]">Gate Entry Module</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <OnlineStatusBadge />
            {isAutoSaving && (
              <div className="hidden sm:flex items-center gap-2 bg-blue-500 px-3 py-2 rounded-lg animate-pulse">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Auto-saving...</span>
              </div>
            )}
            {bundles.length > 0 && (
              <div className="hidden sm:flex items-center gap-2 bg-[#2D5F4F] px-4 py-2 rounded-lg">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">{bundles.length} Bundles</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* GPS status / strict location gate */}
          <div
            className={`rounded-lg border-2 p-4 shadow-sm ${
              hasGps ? 'border-green-500 bg-green-50' : gpsOverride ? 'border-amber-500 bg-amber-50' : 'border-red-400 bg-red-50'
            }`}
            data-testid="gps-status-card"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <MapPin className={`w-6 h-6 mt-0.5 flex-shrink-0 ${hasGps ? 'text-green-600' : gpsOverride ? 'text-amber-600' : 'text-red-600'}`} />
                <div>
                  <p className="font-semibold text-[#1A1F2E]" data-testid="gps-status-text">
                    {hasGps
                      ? 'Location captured'
                      : gpsOverride
                        ? 'Submitting without GPS (admin override)'
                        : geoStatus === 'requesting'
                          ? 'Getting location…'
                          : 'Location required'}
                  </p>
                  <p className="text-sm text-[#5A5F6E]">
                    {hasGps
                      ? `GPS: ${Number(geoCoords.latitude).toFixed(5)}, ${Number(geoCoords.longitude).toFixed(5)} — entry tags the nearest warehouse.`
                      : gpsOverride
                        ? `Approved by ${gpsOverride.by}. Reason: ${gpsOverride.reason}`
                        : geoStatus === 'requesting'
                          ? 'Please allow location access when prompted.'
                          : (geoError || 'Enable location on this device to tag the warehouse.')}
                  </p>
                </div>
              </div>
              {!hasGps && !gpsOverride && (
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={requestGps}
                    disabled={geoStatus === 'requesting'}
                    data-testid="gps-retry-btn"
                    className="border-red-400 text-red-600 hover:bg-red-100"
                  >
                    {geoStatus === 'requesting' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LocateFixed className="w-4 h-4 mr-2" />}
                    Retry GPS
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowGpsOverride(true)}
                    data-testid="gps-override-btn"
                    className="text-amber-700 hover:bg-amber-100"
                  >
                    <ShieldAlert className="w-4 h-4 mr-2" />Admin override
                  </Button>
                </div>
              )}
              {gpsOverride && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setGpsOverride(null)}
                  data-testid="gps-override-clear-btn"
                  className="text-[#5A5F6E] flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Section 1: Staff ID */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">1. Staff Identification</h2>
            <BarcodeInput
              ref={staffIdRef}
              label="Staff ID *"
              value={staffId}
              onChange={setStaffId}
              onScan={handleStaffIdScan}
              placeholder="Scan staff ID barcode"
              disabled={isSubmitting || isSavingDraft}
              error={errors.staffId}
              autoFocus={step === 'staff_id'}
            />
          </div>

          {/* Section 2: Document Barcode */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">2. Document Barcode</h2>
            <BarcodeInput
              ref={documentBarcodeRef}
              label="Document Barcode *"
              value={documentBarcode}
              onChange={setDocumentBarcode}
              onScan={handleDocumentBarcodeScan}
              placeholder="DOCXXXXXX/25-26"
              disabled={isSubmitting || isSavingDraft || !staffId}
              error={errors.documentBarcode}
              autoFocus={step === 'document_barcode'}
              maxLength={25}
            />
            <p className="text-sm text-[#8A8F9E] mt-2">Format: DOCXXXXXX/YY-YY (or SITE-DOCXXXXXX/YY-YY)</p>
          </div>

          {/* Section 3: Image Upload - Hide when editing draft with existing images */}
          {!(isEditingDraft && existingImageUrls.length > 0) && (
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
                3. Document Images *
              </h2>
              <ImageCaptureGrid
                images={images}
                setImages={setImages}
                maxImages={10}
                disabled={isSubmitting || isSavingDraft || !documentBarcode}
              />
              {images.length > 0 && step !== 'bundles' && (
                <div className="mt-4">
                  <Button
                    type="button"
                    onClick={() => setStep('bundles')}
                    className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                    data-testid="continue-to-bundles-btn"
                  >
                    Continue to Bundle Scanning
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Show confirmation when editing draft with existing images */}
          {isEditingDraft && existingImageUrls.length > 0 && (
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
                3. Document Images
              </h2>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 font-medium">
                  ✓ {existingImageUrls.length} image(s) already uploaded to cloud storage
                </p>
              </div>
            </div>
          )}

          {/* Section 4: Bundle QR Scanning */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">
              {isEditingDraft && existingImageUrls.length > 0 ? '4' : '4'}. Scan Bundle QR Codes *
            </h2>
            <BarcodeInput
              ref={bundleBarcodeRef}
              label="Bundle QR Code"
              value={bundleBarcode}
              onChange={setBundleBarcode}
              onScan={handleBundleBarcodeScan}
              placeholder="BUNXXXXXX/25-26"
              disabled={isSubmitting || isSavingDraft || !documentBarcode || (images.length === 0 && existingImageUrls.length === 0)}
              error={errors.bundleBarcode}
              autoFocus={step === 'bundles'}
              maxLength={25}
            />
            <p className="text-sm text-[#8A8F9E] mt-2">Scan multiple bundles continuously.</p>
          </div>

          {/* Section 6: Data Table */}
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm">
            <InwardDataTable
              bundles={bundles}
              onDeleteBundle={handleDeleteBundle}
              documentBarcode={documentBarcode}
              staffId={staffId}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 sticky bottom-4 bg-white rounded-lg border border-[#E5DFD0] p-4 shadow-lg">
            {/* Hide Clear Form in draft edit mode */}
            {!isEditingDraft && (
              <Button
                type="button"
                onClick={handleClear}
                disabled={isSubmitting || isSavingDraft}
                variant="secondary"
                className="flex-1 h-12 border-2 border-[#E5DFD0] hover:border-[#B8374A] hover:text-[#B8374A]"
              >
                Clear Form
              </Button>
            )}
            <Button
              type="button"
              onClick={handleSaveDraft}
              disabled={isSubmitting || isSavingDraft || !staffId || !documentBarcode}
              variant="outline"
              className="flex-1 h-12 border-2 border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-white"
            >
              {isSavingDraft ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Saving Draft...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5 mr-2" />
                  Save as Draft
                </>
              )}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isSavingDraft || !staffId || !documentBarcode || (images.length === 0 && existingImageUrls.length === 0) || bundles.length === 0 || (!hasGps && !gpsOverride)}
              className="flex-1 h-12 bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Submit Entry
                </>
              )}
            </Button>
          </div>
        </form>
      </main>

      {/* Scan Feedback Overlay */}
      {scanFeedback && (
        <ScanFeedbackOverlay
          type={scanFeedback.type}
          message={scanFeedback.message}
          onComplete={() => setScanFeedback(null)}
        />
      )}

      {/* Sync Status Indicator */}
      <SyncStatusIndicator />

      {/* Success Dialog */}
      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center text-2xl text-[#2D5F4F]">
              Success!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center py-6">
              <div className="space-y-3">
                <p className="text-lg font-semibold text-[#1A1F2E]">Entry Number</p>
                <p className="text-3xl font-bold font-mono text-[#2D5F4F] bg-[#F5F1E8] py-4 rounded-lg">
                  {createdEntryId}
                </p>
                <p className="text-sm text-[#5A5F6E] mt-4">
                  {successMessage}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="justify-center">
            <AlertDialogAction
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white px-8"
              onClick={() => {
                setShowSuccessDialog(false);
                setTimeout(() => {
                  if (staffIdRef.current) {
                    staffIdRef.current.focus();
                  }
                }, 100);
              }}
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin GPS Override Dialog */}
      <Dialog open={showGpsOverride} onOpenChange={setShowGpsOverride}>
        <DialogContent className="max-w-md" data-testid="gps-override-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              Admin GPS Override
            </DialogTitle>
            <DialogDescription>
              Location is unavailable. An administrator must approve submitting this
              entry without GPS. The entry stays <strong>Unmapped</strong> until re-tagged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Admin Email</Label>
              <Input
                value={ovEmail}
                onChange={(e) => setOvEmail(e.target.value)}
                placeholder="admin@company.com"
                data-testid="gps-override-email"
                autoComplete="off"
              />
            </div>
            <div>
              <Label className="text-xs">Admin Password</Label>
              <Input
                type="password"
                value={ovPassword}
                onChange={(e) => setOvPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="gps-override-password"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label className="text-xs">Reason *</Label>
              <Input
                value={ovReason}
                onChange={(e) => setOvReason(e.target.value)}
                placeholder="e.g. device GPS hardware fault"
                data-testid="gps-override-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGpsOverride(false)} data-testid="gps-override-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleGpsOverride}
              disabled={ovVerifying}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="gps-override-confirm"
            >
              {ovVerifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Approve &amp; allow submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InwardForm;
